import React, { useState, useMemo, useEffect } from 'react';
import { FormGroup, ControlLabel } from 'react-bootstrap';
import { useDebounce } from 'use-debounce';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ProviderInput from '../../../../../../../../components/Inputs/ProviderInput';

import * as types from '../../reducer/constants';
import { getNumberValue, getProviderFormService } from '../../helper';
import { formattedRateValue, getRateLabel, getSyncDisabled, getSyncMode } from './helper';
import { getErrorMessage, getProvider } from '../../../../../helpers/getters';
import { serviceServicesGetSyncRate } from '../../../../../../../../services/endpoints/services';
import Alert from '../../../../../../../../components/Alert';

const FieldRate = ({ form, isLoadingRate, errorProvider, isLoadingProvider, providerForm, dispatchServiceData, modal }) => {
  const { provider_id, provider_service_id, rate, auto_rate, auto_rate_fixed, auto_rate_percent, sync_error, last_success_sync } = form;
  const service = useMemo(() => getProviderFormService(provider_service_id, providerForm), [provider_service_id, providerForm]);
  const rateLabel = useMemo(() => getRateLabel(form, service), [form, service]);
  const provider = useMemo(() => getProvider(provider_id), [provider_id]);
  const syncMode = useMemo(
    () => getSyncMode(provider, service, errorProvider, isLoadingProvider),
    [provider, service, errorProvider, isLoadingProvider],
  );
  const syncModeDisabled = useMemo(
    () => getSyncDisabled(provider),
    [provider],
  );
  const syncRate = service ? service.service_rate : null;
  const serviceRateConverted = service?.service_rate_converted ? service.service_rate_converted : null;
  const providerCurrency = provider?.currency;

  const [autoRateFixedDebounce] = useDebounce(auto_rate_fixed, 500);
  const [autoRatePercentDebounce] = useDebounce(auto_rate_percent, 500);

  const [stackRequest, setStackRequest] = useState([]);
  const [errorSync, setErrorSync] = useState(null);

  const alertSyncExternalMessageError = `The provider API has not responded to last requests regarding the rate for this service. The indicated rate may be incorrect. Please ensure the rate correctness for this service in your account on the provider's website.<br /> Last successful sync: ${last_success_sync || ''}`
  const alertSyncExternalMessageWarning = `The service rate is calculated based on the external provider's API response. Using external sync has potential risks, including receiving incorrect data from the provider, currency update delays, or receiving amounts in a different currency<br /> Last successful sync: ${last_success_sync || ''}`

  const dispatchRate = (value) => {
    dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE, value });
  };

  const getRate = async () => {
    try {
      const rates = [
        {
          provider_service_id,
          fixed: auto_rate_fixed,
          percent: auto_rate_percent,
        },
      ];
      const response = await serviceServicesGetSyncRate(provider_id, rates);
      if (response?.data) {
        const { data } = response;
        if (_.isArray(data)) {
          if (data[0]?.hasOwnProperty('rate')) {
            setStackRequest((prevState) => prevState.filter((item, index) => index !== 0));
            dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_WITH_SYNC, value: data[0]?.rate });
          }
        }
      }
    } catch (error) {
      setStackRequest((prevState) => prevState.filter((item, index) => index !== 0));
      const errorMessage = getErrorMessage(error);
      setErrorSync(errorMessage);
    }
  };

  const onChangeRate = (e) => {
    const value = formattedRateValue(e.target.value);
    if (value === '') {
      dispatchRate(null);
    } else {
      const submitValue = value.replace(',', '.');
      if (/^[0-9]+(?:\.|\.[0-9]{0,4})?$/.test(submitValue)) {
        dispatchRate(submitValue);
      }
    }
  };

  const onChangeRateFixed = (e) => {
    const value = e.target.value;
    if (value === '') {
      dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_FIXED, value: null });
    } else {
      const submitValue = value.replace(',', '.');
      if (/^[0-9]+(?:\.|\.[0-9]+)?$/.test(submitValue)) {
        dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_FIXED, value: submitValue });
      }
    }
  };

  const onChangeRatePercent = (e) => {
    const value = e.target.value;
    const percentValue = getNumberValue(value);
    if (!_.isBoolean(percentValue)) {
      dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_PERCENT, value: percentValue });
    }
  };

  const onChangeSync = (value) => {
    dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_SYNC_RATE, value });
  };

  useEffect(() => {
    if (stackRequest.length) {
      getRate();
    } else {
      dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_LOADING, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackRequest]);

  useEffect(() => {
    if (auto_rate) {
      setStackRequest((prevState) => [...prevState, true]);
    }
    if (errorSync) {
      setErrorSync(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRateFixedDebounce, autoRatePercentDebounce, auto_rate, provider_service_id]);

  useEffect(() => {
    if (!syncMode) {
      onChangeSync(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auto_rate_fixed === autoRateFixedDebounce && isLoadingRate) {
      dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_LOADING, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto_rate_fixed]);

  useEffect(() => {
    if (auto_rate_percent === autoRatePercentDebounce && isLoadingRate) {
      dispatchServiceData({ type: types.EDIT_SERVICES_FORM_CHANGE_RATE_LOADING, value: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto_rate_percent]);

  return (
    <>
      <FormGroup className="form-group__rate">
        <ControlLabel htmlFor="rate">{rateLabel}</ControlLabel>
        <ProviderInput
          value={_.isNull(rate) ? '' : rate}
          name="rate"
          syncMode={syncMode ? 'rate' : false}
          syncDisabled={syncModeDisabled}
          rateFixed={auto_rate_fixed}
          ratePercent={auto_rate_percent}
          /* Если service_auto_rate = false - выводим переключательно и дизейблим */
          syncValue={service && auto_rate && !syncModeDisabled}
          isLoading={isLoadingRate || stackRequest.length}
          onChange={onChangeRate}
          onChangeSync={onChangeSync}
          onChangeRateFixed={onChangeRateFixed}
          onChangeRatePercent={onChangeRatePercent}
          providerValue={syncRate}
          serviceRateConverted={serviceRateConverted}
          providerCurrency={providerCurrency}
        />
        {syncMode && errorSync ? <small className="text-danger">{errorSync}</small> : null}
      </FormGroup>
      {/* Показываем Alert только для внешних провайдеров type = 0 */}
      {provider?.type === 0 && auto_rate && modal?.id
        ? (
          <Alert
            type={sync_error ? 'danger' : 'warning'}
            text={sync_error ? alertSyncExternalMessageError : alertSyncExternalMessageWarning}
          />
        ) : null}
    </>
  );
};

FieldRate.propTypes = {
  form: PropTypes.any,
  isLoadingRate: PropTypes.any,
  errorProvider: PropTypes.any,
  isLoadingProvider: PropTypes.any,
  providerForm: PropTypes.any,
  dispatchServiceData: PropTypes.func,
  modal: PropTypes.object,
};

export default FieldRate;
